import _isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { IReviewsListState } from 'src/store/reducers/reviews';

import { ILocationV2 } from '@sravni/types/lib/locations';

import { IMetadata } from '../interfaces/metadata';
import { IItem, OrganizationType } from '../interfaces/organizations';
import { IReviewItem } from '../interfaces/reviews';
import { TMetadataParams } from '../server/services/metadata';
import { GlobalState } from '../store';
import { selectServerFiltersByType } from '../store/selectors/filters';
import { getCenters, getCurrentLocation, getRegions } from '../store/selectors/locations';
import { selectOrganizationDetails } from '../store/selectors/organizations';
import { selectCurrentReviewType, selectReviewItem, selectReviewsList } from '../store/selectors/reviews';
import { ALL_ROUTER_QUERIES } from '../types/router';

import { normalizeLocation } from './decorators';
import { getTagByProduct, ReviewType, shrinkText } from './reviews';

export type TPageType = 'list' | 'review' | 'createReview';

type ISeo = IMetadata;

const ratingSeoScheme = (item) => {
  return {
    itemReviewed: {
      image: item.logo && item.logo.squareLogo,
      name: item.organizationName,
      '@context': 'http://schema.org',
      '@type': 'Organization',
    },
    '@context': 'http://schema.org',
    '@type': 'AggregateRating',
    bestRating: '5',
    ratingValue: '5',
    reviewCount: '10',
    worstRating: '0',
  };
};

const reviewSeoScheme = (item: IReviewItem) => {
  return {
    itemReviewed: {
      image: item.logo && item.logo.square,
      name: item.organizationName,
      '@context': 'http://schema.org',
      '@type': 'Organization',
    },
    reviewBody: item.text,
    reviewRating: {
      bestRating: '5',
      ratingValue: item.rating,
      worstRating: '0',
      '@context': 'http://schema.org',
      '@type': 'Rating',
    },
    author: {
      name: item.authorName,
      '@context': 'http://schema.org',
      '@type': 'Person',
    },
    commentCount: item.commentsCount,
    contentLocation: {
      name: item.locationData?.name,
      '@context': 'http://schema.org',
      '@type': 'Place',
    },
    datePublished: item.date,
    headline: item.title,
    '@context': 'http://schema.org',
    '@type': 'Review',
  };
};

const reviewItemSeoScheme = (item) => {
  const scheme = {
    '@type': 'Review',
    author: item.authorName,
    datePublished: item.date,
    reviewBody: item.text,
    name: item.title,
    reviewRating: null,
  };

  if (item.rating && +item.rating > 0) {
    scheme.reviewRating = {
      '@type': 'Rating',
      ratingValue: item.rating,
      bestRating: '5',
      worstRating: '0',
    };
  }

  return scheme;
};

const reviewsListSeoScheme = (list: IReviewItem[]) => {
  return list.map((item) => reviewItemSeoScheme(item));
};

const getKeywords = (item) => {
  return item.keywords || '';
};

export const makeReviewMetadata = (
  seo: Partial<ISeo>,
  item: IReviewItem,
  type: OrganizationType,
  itemTypeUrl: string,
) => {
  if (_isEmpty(item)) return { title: 'Отзывы на Sravni.ru' };

  const { organizationName, locationData, alias, id } = item;

  const text = item.title || item.text;
  let title = text ? shrinkText(text) : '';
  const description = text ? shrinkText(item.text, 200) : '';
  const canonical = `https://www.sravni.ru/${itemTypeUrl}/${alias}/otzyvy/${id}/`;

  if (type === OrganizationType.BANK) {
    title = `Сравни.ру: ${organizationName}, ${locationData?.name}: Отзыв: ${title}`;
  } else {
    title = `Отзыв о компании ${organizationName}, ${locationData?.name}: ${title} — Сравни.ру`;
  }

  const reviewMeta = [
    { name: 'keywords', content: getKeywords(item) },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: `https://www.sravni.ru/${type}/${alias}/otzyvy/${id}` },
  ];

  return {
    ...seo,
    title,
    description,
    canonical,
    meta: reviewMeta,
    schema: {
      ...seo?.schema,
      aggregateRating: ratingSeoScheme(item),
      review: [reviewSeoScheme(item)],
    },
  };
};

export const makeListMetadata = (seo: Partial<ISeo>, listState: IReviewsListState, orgDetails?: IItem) => {
  const { items: list, total } = listState;

  let ratingValue;

  if (!Object.keys(orgDetails).length) {
    const ratingSum = list.reduce((output, item) => {
      output += item.rating;
      return output;
    }, 0);

    ratingValue = list.length && ratingSum && ratingSum / list.length > 1 ? ratingSum / list.length : 1;
  } else {
    ratingValue = orgDetails?.position?.complexCalculatedRatingValue.toFixed(2);
  }

  return {
    ...seo,
    schema: [
      {
        '@context': 'http://schema.org',
        '@type': 'Product',
        // @ts-ignore
        name: seo?.schema?.name || 'Отзывы о банках',
        aggregateRating: {
          bestRating: '5',
          worstRating: '0',
          ratingCount: `${total}`,
          ratingValue: `${ratingValue}`,
        },
        reviews: reviewsListSeoScheme(list),
      },
      { ...seo?.schema },
    ],
  };
};

export const makeCreateReviewMetadata = (reviewType?: ReviewType): IMetadata => {
  const name = reviewType?.namesType?.mode6;

  const title = name ? `Добавить отзыв о ${name}` : 'Добавить отзыв';

  // @ts-ignore
  return { title };
};

export const prepareSeoData = (data: IMetadata, pageType: TPageType, state: GlobalState, orgAlias?: string) => {
  let seoData;
  const reviewType = selectCurrentReviewType(state);

  switch (pageType) {
    case 'list':
      const list = selectReviewsList(state);
      const orgDetails = selectOrganizationDetails(orgAlias)(state);
      seoData = makeListMetadata(data, list, orgDetails);
      break;
    case 'review':
      const { item } = selectReviewItem(state);
      seoData = makeReviewMetadata(data, item, reviewType.typeForService, reviewType.itemTypeUrl);
      break;
    // case 'createReview':
    //   seoData = makeCreateReviewMetadata(reviewType);
  }
  return seoData;
};

export const makeSeoPayload = (query: ALL_ROUTER_QUERIES, pathname: string, state: GlobalState): TMetadataParams => {
  const { organization, location: locationGarRoute, regionId, product } = query;

  const centers = getCenters(state);
  const regions = getRegions(state);
  const typePage = selectCurrentReviewType(state);
  const reviewObjectType = typePage.typeForService;

  const location: ILocationV2 = centers[locationGarRoute] || regions[regionId] || getCurrentLocation(state) || {};
  const organizationDetails = selectOrganizationDetails(organization)(state);

  const organizationPayload = !_isEmpty(organizationDetails)
    ? {
        ...pick(organizationDetails, ['id', 'name', 'alias', 'type']),
        type: typePage.seoService || typePage.typeForService,
      }
    : null;

  const reviewTag = product
    ? {
        id: 1,
        name: getTagByProduct(selectServerFiltersByType(reviewObjectType)(state).tag, product)?.name,
        alias: product,
      }
    : null;

  const payload = {
    url: pathname,
    location: normalizeLocation(location),
    ...(organizationPayload && { organization: organizationPayload }),
    ...(reviewTag && { reviewTag }),
  };

  // @ts-ignore
  return payload;
};

export const preparePath = (pathname: string) => {
  // remove search params
  pathname = pathname.split('?')[0];
  // remove hash params
  pathname = pathname.split('#')[0];
  // remove closing slash
  return pathname.endsWith('/') ? pathname : `${pathname}/`;
};
